import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Download, UploadCloud, FileSpreadsheet, Loader2, CheckCircle2, XCircle,
  AlertTriangle, ArrowRight, Info, Trash2, History, Eye,
} from 'lucide-react';

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BULK_STATUS = {
  ready: { label: 'Ready', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  validation_failed: { label: 'Validation Failed', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
  processing: { label: 'Processing', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  created: { label: 'Created', cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300' },
};

const StatusPill = ({ s }) => {
  const m = BULK_STATUS[s] || { label: s, cls: 'bg-muted text-muted-foreground' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>{m.label}</span>;
};

export const BulkPayout = ({ api }) => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [validating, setValidating] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [idemKey] = useState(() => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())));
  const [history, setHistory] = useState([]);
  const [detail, setDetail] = useState(null);
  const [formatOpen, setFormatOpen] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get('/bulk-payouts');
      setHistory(res.data?.uploads || []);
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const downloadFile = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const pickFile = (f) => {
    if (!f) return;
    const ok = /\.(xlsx|csv)$/i.test(f.name);
    if (!ok) { toast.error('Please upload a .xlsx or .csv file'); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error('File must be 10MB or smaller'); return; }
    setFile(f); setResult(null); setProgress({ done: 0, total: 0 });
    // Best-effort row count for the progress bar (CSV only; xlsx is opaque in-browser)
    if (/\.csv$/i.test(f.name)) {
      const reader = new FileReader();
      reader.onload = () => {
        const lines = String(reader.result || '').split(/\r?\n/).filter((l) => l.trim()).length;
        setProgress({ done: 0, total: Math.max(0, lines - 1) });
      };
      reader.readAsText(f);
    }
  };

  const validate = async () => {
    if (!file) return;
    setValidating(true); setResult(null);
    const total = progress.total;
    // Animate the progress toward ~90% while the request is in flight
    let done = 0;
    const step = Math.max(1, Math.ceil((total || 100) / 40));
    const timer = setInterval(() => {
      done = Math.min((total || 100) * 0.9, done + step);
      setProgress((p) => ({ ...p, done: Math.round(done) }));
    }, 60);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/bulk-payouts/validate', fd, { headers: { 'Content-Type': undefined } });
      clearInterval(timer);
      setProgress({ done: res.data.total_rows, total: res.data.total_rows });
      setResult(res.data);
      if (res.data.all_valid && res.data.sufficient_balance) {
        toast.success(`${res.data.valid_rows} rows validated successfully`);
      } else if (!res.data.all_valid) {
        toast.error(`${res.data.invalid_rows} row(s) need correction`);
      } else if (!res.data.sufficient_balance) {
        toast.error('Insufficient wallet balance');
      }
    } catch (e) {
      clearInterval(timer);
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const confirmCreate = async () => {
    if (!result?.bulk_upload_id) return;
    setCreating(true);
    try {
      const res = await api.post(`/bulk-payouts/${result.bulk_upload_id}/confirm`, {}, {
        headers: { 'Idempotency-Key': idemKey },
      });
      setConfirmOpen(false);
      toast.success(res.data.message || 'Bulk payout created');
      setFile(null); setResult(null);
      await loadHistory();
      navigate('/dashboard/payouts');
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to create bulk payout');
    } finally {
      setCreating(false);
    }
  };

  const canCreate = result?.all_valid && result?.sufficient_balance;

  return (
    <div className="space-y-6" data-testid="bulk-payout-panel">
      {/* Steps header */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-primary" />Bulk Payout
          </CardTitle>
          <p className="text-sm text-muted-foreground">Upload an Excel or CSV file to create multiple payout orders at once.</p>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-3 gap-4">
          {[
            ['1', 'Download Sample', 'Get the official AwadhPay template'],
            ['2', 'Upload File', 'Upload your completed Excel/CSV'],
            ['3', 'Review & Create', 'Validate, check balance and create'],
          ].map(([n, t, d]) => (
            <div key={n} className="flex items-start gap-3 rounded-lg border p-3 bg-muted/20">
              <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold shrink-0">{n}</span>
              <div>
                <p className="font-medium text-sm">{t}</p>
                <p className="text-xs text-muted-foreground">{d}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Download sample + format */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="py-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <p className="font-semibold">Don't have the correct format?</p>
            <p className="text-sm text-muted-foreground">Use our official template to prepare your payout orders.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setFormatOpen(true)} data-testid="view-format-btn">
              <Info className="w-4 h-4 mr-2" />View Format
            </Button>
            <Button variant="outline" onClick={() => downloadFile('/bulk-payouts/template?format=csv', 'awadhpay_bulk_payout_template.csv')} data-testid="download-sample-csv-btn">
              <Download className="w-4 h-4 mr-2" />CSV
            </Button>
            <Button onClick={() => downloadFile('/bulk-payouts/template?format=xlsx', 'awadhpay_bulk_payout_template.xlsx')} data-testid="download-sample-btn">
              <Download className="w-4 h-4 mr-2" />Download Sample Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Upload zone */}
      <Card>
        <CardContent className="py-6">
          <div
            className={`rounded-xl border-2 border-dashed p-8 text-center transition-colors ${dragOver ? 'border-primary bg-primary/5' : 'border-border'}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]); }}
            data-testid="bulk-dropzone"
          >
            <UploadCloud className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileSpreadsheet className="w-5 h-5 text-primary" />
                <span className="font-medium" data-testid="bulk-file-name">{file.name}</span>
                <span className="text-xs text-muted-foreground">({(file.size / 1024).toFixed(1)} KB)</span>
                <Button size="icon" variant="ghost" onClick={() => { setFile(null); setResult(null); }} data-testid="bulk-remove-file">
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground mb-3">Drag &amp; drop your Excel / CSV here, or</p>
                <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="bulk-browse-btn">Browse Files</Button>
              </>
            )}
            <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])} data-testid="bulk-file-input" />
          </div>

          {file && (
            <div className="flex justify-end mt-4">
              <Button onClick={validate} disabled={validating} data-testid="bulk-validate-btn">
                {validating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Validate File <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {validating && (
            <div className="mt-4" data-testid="bulk-progress">
              <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                <span>Validating rows…</span>
                <span className="font-mono">
                  {progress.total ? `${Math.min(progress.done, progress.total)} / ${progress.total} rows` : 'Please wait…'}
                </span>
              </div>
              <Progress value={progress.total ? Math.min(100, (progress.done / progress.total) * 100) : undefined} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Validation result */}
      {result && (
        <Card data-testid="bulk-review">
          <CardHeader className="pb-3"><CardTitle className="text-lg font-heading">Review Bulk Payout</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                ['Total Rows', result.total_rows, ''],
                ['Valid Orders', result.valid_rows, 'text-green-600'],
                ['Invalid Rows', result.invalid_rows, result.invalid_rows > 0 ? 'text-red-600' : ''],
                ['Payout Amount', fmtINR(result.total_amount), ''],
                ['Fees + Tax', fmtINR(result.total_fees), ''],
                ['Total Debit', fmtINR(result.total_debit), 'text-primary'],
              ].map(([label, val, cls]) => (
                <div key={label} className="rounded-lg border p-3 bg-muted/20">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className={`text-lg font-bold font-mono tabular-nums ${cls}`}>{val}</p>
                </div>
              ))}
            </div>

            {/* Balance check */}
            <div className={`rounded-lg border p-4 ${canCreate ? 'border-green-500/40 bg-green-500/5' : 'border-red-500/40 bg-red-500/5'}`} data-testid="bulk-balance-check">
              <div className="grid sm:grid-cols-3 gap-3 text-sm">
                <div><span className="text-muted-foreground">Available Balance</span><p className="font-mono font-semibold">{fmtINR(result.available_balance)}</p></div>
                <div><span className="text-muted-foreground">Total Required</span><p className="font-mono font-semibold">{fmtINR(result.total_debit)}</p></div>
                <div><span className="text-muted-foreground">{result.sufficient_balance ? 'Remaining Balance' : 'Shortfall'}</span>
                  <p className={`font-mono font-semibold ${result.sufficient_balance ? '' : 'text-red-600'}`}>
                    {result.sufficient_balance ? fmtINR(result.remaining_balance) : fmtINR(result.shortfall)}
                  </p>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 text-sm font-medium">
                {result.sufficient_balance
                  ? <><CheckCircle2 className="w-4 h-4 text-green-600" />Sufficient wallet balance</>
                  : <><XCircle className="w-4 h-4 text-red-600" />Insufficient wallet balance</>}
              </div>
            </div>

            {/* Error table */}
            {result.errors?.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium flex items-center gap-2 text-red-600"><AlertTriangle className="w-4 h-4" />{result.errors.length} row(s) need correction</p>
                  <Button size="sm" variant="outline" onClick={() => downloadFile(`/bulk-payouts/${result.bulk_upload_id}/error-report?format=csv`, `${result.bulk_id}_errors.csv`)} data-testid="download-error-report-btn">
                    <Download className="w-4 h-4 mr-2" />Download Error Report
                  </Button>
                </div>
                <div className="rounded-lg border overflow-x-auto">
                  <table className="w-full text-sm min-w-[520px]" data-testid="bulk-error-table">
                    <thead><tr className="border-b text-left text-muted-foreground bg-muted/30">
                      <th className="py-2 px-3">Row</th><th className="py-2 px-3">Beneficiary</th><th className="py-2 px-3">Amount</th><th className="py-2 px-3">Errors</th>
                    </tr></thead>
                    <tbody>
                      {result.errors.map((er) => (
                        <tr key={er.row} className="border-b last:border-0">
                          <td className="py-2 px-3 font-mono">Row {er.row}</td>
                          <td className="py-2 px-3">{er.beneficiary || '—'}</td>
                          <td className="py-2 px-3 font-mono">{fmtINR(er.amount)}</td>
                          <td className="py-2 px-3 text-red-600">{er.errors.join('; ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-muted-foreground mt-2">Please correct the highlighted rows and upload the file again.</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setFile(null); setResult(null); }}>Upload Different File</Button>
              <Button onClick={() => setConfirmOpen(true)} disabled={!canCreate} data-testid="bulk-create-btn">
                Create {result.valid_rows} Payouts <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk history */}
      <Card data-testid="bulk-history">
        <CardHeader className="pb-3"><CardTitle className="text-lg font-heading flex items-center gap-2"><History className="w-5 h-5" />Bulk Payout History</CardTitle></CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No bulk uploads yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead><tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 px-3">Bulk ID</th><th className="py-2 px-3">Date</th><th className="py-2 px-3">Orders</th>
                  <th className="py-2 px-3">Amount</th><th className="py-2 px-3">Fees</th><th className="py-2 px-3">Status</th><th className="py-2 px-3"></th>
                </tr></thead>
                <tbody>
                  {history.map((u) => (
                    <tr key={u.id} className="border-b last:border-0 hover:bg-accent/40" data-testid={`bulk-row-${u.bulk_id}`}>
                      <td className="py-2 px-3 font-mono">{u.bulk_id}</td>
                      <td className="py-2 px-3 text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleString('en-IN') : '—'}</td>
                      <td className="py-2 px-3">{u.valid_rows}</td>
                      <td className="py-2 px-3 font-mono">{fmtINR(u.total_amount)}</td>
                      <td className="py-2 px-3 font-mono">{fmtINR((u.total_fees || 0) + (u.total_tax || 0))}</td>
                      <td className="py-2 px-3"><StatusPill s={u.status} /></td>
                      <td className="py-2 px-3">
                        <Button size="sm" variant="ghost" onClick={() => setDetail(u)} data-testid={`bulk-view-${u.bulk_id}`}><Eye className="w-4 h-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="bulk-confirm-modal">
          <DialogHeader><DialogTitle>Confirm Bulk Payout</DialogTitle>
            <DialogDescription>Review the totals below. Orders will be sent to Admin for approval.</DialogDescription>
          </DialogHeader>
          {result && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Total Orders</span><span className="font-semibold">{result.valid_rows}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Payout Amount</span><span className="font-mono">{fmtINR(result.total_amount)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Fees</span><span className="font-mono">{fmtINR(result.total_fee_only)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Taxes</span><span className="font-mono">{fmtINR(result.total_tax)}</span></div>
              <div className="flex justify-between border-t pt-2 font-semibold"><span>Total Debit</span><span className="font-mono text-primary">{fmtINR(result.total_debit)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Available Balance</span><span className="font-mono">{fmtINR(result.available_balance)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Remaining Balance</span><span className="font-mono">{fmtINR(result.remaining_balance)}</span></div>
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-300 flex gap-2 mt-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />Once submitted, these payout orders will be sent to Admin for approval.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={creating}>Cancel</Button>
            <Button onClick={confirmCreate} disabled={creating} data-testid="bulk-confirm-create-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create {result?.valid_rows} Payouts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Format requirements */}
      <Dialog open={formatOpen} onOpenChange={setFormatOpen}>
        <DialogContent data-testid="format-modal">
          <DialogHeader><DialogTitle>Format Requirements</DialogTitle>
            <DialogDescription>Columns your Excel/CSV file must contain.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            {[
              ['beneficiary_name', 'Required · 2–100 characters'],
              ['account_number', 'Required · digits only (6–20)'],
              ['ifsc', 'Required · format AAAA0XXXXXX (auto-uppercased)'],
              ['amount', 'Required · INR · within your min/max payout limits'],
              ['reference', 'Optional · up to 50 characters (recommended)'],
            ].map(([c, d]) => (
              <div key={c} className="flex justify-between gap-4 border-b pb-1.5 last:border-0">
                <code className="font-mono text-primary">{c}</code><span className="text-muted-foreground text-right">{d}</span>
              </div>
            ))}
            <p className="text-xs text-muted-foreground pt-2">Fees, GST and limits follow your account's payout configuration. Duplicate rows are rejected. If any row is invalid, no payouts are created (all-or-nothing).</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <BulkDetailDialog api={api} upload={detail} onClose={() => setDetail(null)} downloadFile={downloadFile} />
    </div>
  );
};

const BulkDetailDialog = ({ api, upload, onClose, downloadFile }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!upload) { setData(null); return; }
    api.get(`/bulk-payouts/${upload.id}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [upload, api]);
  return (
    <Dialog open={!!upload} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="bulk-detail">
        <DialogHeader><DialogTitle>Bulk {upload?.bulk_id}</DialogTitle>
          <DialogDescription>Individual payout orders in this batch.</DialogDescription>
        </DialogHeader>
        {data ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <StatusPill s={data.upload.status} />
              <div className="flex gap-2">
                {data.upload.invalid_rows > 0 && (
                  <Button size="sm" variant="outline" onClick={() => downloadFile(`/bulk-payouts/${upload.id}/error-report?format=csv`, `${upload.bulk_id}_errors.csv`)} data-testid="detail-error-report-btn">
                    <Download className="w-4 h-4 mr-2" />Error Report
                  </Button>
                )}
                {data.upload.status === 'created' && (
                  <Button size="sm" variant="outline" onClick={() => downloadFile(`/bulk-payouts/${upload.id}/results?format=csv`, `${upload.bulk_id}_results.csv`)} data-testid="download-results-btn">
                    <Download className="w-4 h-4 mr-2" />Download Results
                  </Button>
                )}
              </div>
            </div>
            <div className="rounded-lg border overflow-x-auto max-h-80">
              <table className="w-full text-sm min-w-[520px]">
                <thead><tr className="border-b text-left text-muted-foreground bg-muted/30">
                  <th className="py-2 px-3">Order ID</th><th className="py-2 px-3">Beneficiary</th><th className="py-2 px-3">Amount</th><th className="py-2 px-3">Status</th>
                </tr></thead>
                <tbody>
                  {(data.orders || []).map((o) => (
                    <tr key={o.id} className="border-b last:border-0">
                      <td className="py-2 px-3 font-mono">{o.order_id}</td>
                      <td className="py-2 px-3">{o.beneficiary_name}</td>
                      <td className="py-2 px-3 font-mono">{fmtINR(o.amount)}</td>
                      <td className="py-2 px-3"><Badge variant="secondary">{o.status}</Badge></td>
                    </tr>
                  ))}
                  {(!data.orders || data.orders.length === 0) && (
                    <tr><td colSpan={4} className="py-4 text-center text-muted-foreground">No orders created for this batch.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}
      </DialogContent>
    </Dialog>
  );
};

export default BulkPayout;
